import type { Module } from 'vuex'
import type { StoreState } from '../store-state'

/**
 * 시트 칸의 너비·높이 — 병원별로 기억한다 (vuex-persistedstate 가 localStorage 에 얹는다).
 *
 * width  : 담당자 열마다 하나
 * height : 요일·시간간격 조합마다 그 날의 시간 칸 목록 전체
 */
export type StoredColumnWidth = {
    _id: string
    width: number
}

export type StoredDayHeight = {
    dayIndex: number
    interval: number
    values: { height: number; [extra: string]: unknown }[]
}

export type V2ReservationCellConfigState = {
    defaultMinimumWidth: number
    defaultWidth: number
    defaultHeight: number
    width: Record<string, StoredColumnWidth[] | undefined>
    height: Record<string, StoredDayHeight[] | undefined>
}

const state: V2ReservationCellConfigState = {
    defaultMinimumWidth: 100,
    defaultWidth: 160,
    defaultHeight: 26,
    width: {},
    height: {},
}

const getters = {
    CellConfig_FindColumnWidth:
        (state: V2ReservationCellConfigState) =>
        (hospitalID: string, columnID: string): StoredColumnWidth | [] =>
            state.width[hospitalID]?.find((w) => w._id == columnID) ?? [],
}

const mutations = {
    'MU_V2ReservationCellWidth': (
        state: V2ReservationCellConfigState,
        param: { hospitalID: string; columns: StoredColumnWidth[] },
    ): void => {
        const hospitalID = param.hospitalID
        const columns = param.columns
        const origins = state.width[hospitalID] ?? []
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i]
            const target = origins.find((w) => w._id == column._id)
            if (target == undefined) { origins.push(column) }
            else { origins.splice(origins.indexOf(target), 1, column) }
        }

        // 첫 병원은 객체를 통째로 갈아끼워야 한다 — Vue 2 는 새로 더한 키를 감지하지 못한다
        if (Object.keys(state.width)?.length == 0) {
            const newWidth: V2ReservationCellConfigState['width'] = {}
            newWidth[hospitalID] = origins
            state.width = newWidth
        }
        else {
            state.width[hospitalID] = origins
        }
    },
    /** 시트 칸의 높이를 업데이트한다 */
    'MU_V2ReservationCellHeight': (
        state: V2ReservationCellConfigState,
        param: { hospitalID: string; data?: StoredDayHeight },
    ): void => {
        const hospitalID = param.hospitalID
        const data = param.data
        // 원본은 data 가 없으면 빈 배열을 밀어 넣었다. 호출처(setTimeArray)는 늘 채워 보내므로
        // 닿지 않는 길이고, 쓰레기를 남기지 않도록 여기서 끝낸다
        if (data == undefined) { return }
        if (state.height[hospitalID] == undefined) { state.height[hospitalID] = [] }
        const days = state.height[hospitalID] as StoredDayHeight[]
        const index = days.findIndex((v) => v.dayIndex == data.dayIndex && v.interval == data.interval)
        if (index != -1) { days.splice(index, 1, data) }
        else { days.push(data) }
    },
}

const V2ReservationCellConfig: Module<V2ReservationCellConfigState, StoreState> = { state, getters, mutations }

export default V2ReservationCellConfig
